import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Bot,
  Calendar,
  Clock,
  History,
  Lightbulb,
  Loader2,
  MessageSquare,
  Mic,
  Save,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  User,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import AgentPageShell from '../../components/agent/AgentPageShell';
import ConfidenceBar from '../../components/agent/ConfidenceBar';
import { ResolutionBadge, FeedbackBadge } from '../../components/agent/StatusBadge';
import { cn } from '../../lib/utils';
import {
  deleteSession,
  fetchSession,
  submitMessageFeedback,
  updateSession,
} from '../../lib/agentApi';
import { formatWhen } from './AgentDashboardPage';

const TranscriptDetailPage = () => (
  <AgentPageShell
    title="Transcript"
    description="One conversation, with its sources, confidence and escalation trail"
    icon={BookOpen}
    breadcrumb={['Chat Logs', 'Transcript']}
    actions={
      <Button variant="outline" size="sm" asChild>
        <Link to="/dashboard/agent/chat-logs">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Chat Logs
        </Link>
      </Button>
    }
  >
    {({ agent, orgId }) => <TranscriptBody agent={agent} orgId={orgId} />}
  </AgentPageShell>
);

const TranscriptBody = ({ agent, orgId }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchSession(orgId, agent.id, sessionId);
      setData(result);
      setNote(result.session.internal_note || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the transcript');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await updateSession(orgId, agent.id, sessionId, { internal_note: note });
      toast.success('Note saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the note');
    } finally {
      setSavingNote(false);
    }
  };

  const addTag = async () => {
    const tag = tagDraft.trim();
    if (!tag) return;
    const tags = [...new Set([...(data.session.tags || []), tag])];
    try {
      const session = await updateSession(orgId, agent.id, sessionId, { tags });
      setData((d) => ({ ...d, session }));
      setTagDraft('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the tag');
    }
  };

  const removeTag = async (tag) => {
    const tags = (data.session.tags || []).filter((t) => t !== tag);
    try {
      const session = await updateSession(orgId, agent.id, sessionId, { tags });
      setData((d) => ({ ...d, session }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove the tag');
    }
  };

  const rate = async (messageId, feedback) => {
    try {
      await submitMessageFeedback(orgId, agent.id, messageId, { feedback });
      toast.success('Feedback recorded');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record feedback');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSession(orgId, agent.id, sessionId);
      toast.success('Session deleted');
      navigate('/dashboard/agent/chat-logs');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete the session');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading transcript…
      </div>
    );
  }
  if (!data) return null;

  const { session, messages, sources, similar_sessions: similar } = data;
  const config = agent.published_config || agent.config || {};

  return (
    <div className="space-y-6">
      {/* Meta bar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-x-8 gap-y-4">
          <Meta label="Session" value={`#${session.id.slice(0, 8)}`} mono />
          <Meta
            label="Channel"
            value={
              <span className="flex items-center gap-1.5">
                {session.channel === 'voice' ? (
                  <Mic className="w-3.5 h-3.5 text-brand-blue" />
                ) : (
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-500" />
                )}
                <span className="capitalize">{session.channel}</span>
              </span>
            }
          />
          <Meta label="Language" value={(session.language || '—').toUpperCase()} />
          <Meta
            label="Started"
            value={
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                {new Date(session.created_at).toLocaleString()}
              </span>
            }
          />
          <Meta
            label="Messages"
            value={
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                {messages.length}
              </span>
            }
          />
          <div className="flex items-center gap-2 ml-auto">
            <ResolutionBadge state={session.resolution_state} />
            <FeedbackBadge feedback={session.feedback} />
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Thread */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-6 space-y-2">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  config={config}
                  onRate={(feedback) => rate(msg.id, feedback)}
                />
              ))}
            </CardContent>
          </Card>

          {/* Internal notes */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <h3 className="text-sm font-semibold">Internal Notes</h3>
              <Textarea
                placeholder="Add a private note for the team…"
                className="min-h-[100px] resize-none bg-muted/30"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap flex-1">
                  {(session.tags || []).map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeTag(tag)}
                      title="Click to remove"
                    >
                      {tag} ×
                    </Badge>
                  ))}
                  <Input
                    placeholder="Add tag…"
                    className="h-7 w-28 text-xs"
                    value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTag()}
                  />
                </div>
                <Button size="sm" onClick={saveNote} disabled={savingNote}>
                  {savingNote ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Note
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Similar sessions */}
          {similar.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <History className="w-4 h-4 text-brand-blue" />
                Similar Sessions
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {similar.map((other) => (
                  <Link
                    key={other.id}
                    to={`/dashboard/agent/chat-logs/${other.id}`}
                    className="bg-card border border-border rounded-lg p-3 hover:shadow-md hover:border-primary/40 transition-all"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-mono font-bold text-brand-blue">
                        #{other.id.slice(0, 8)}
                      </span>
                      <Badge
                        variant="outline"
                        className="text-[10px] h-4 px-1.5 bg-emerald-500/5 text-emerald-600 border-emerald-500/20"
                      >
                        {other.match}% match
                      </Badge>
                    </div>
                    <p
                      className="text-xs text-muted-foreground truncate"
                      dir={other.language === 'ar' ? 'rtl' : 'ltr'}
                    >
                      {other.first_message}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="text-sm font-semibold">Session Intelligence</h3>
              <div className="space-y-1">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-muted-foreground">Retrieval confidence</span>
                  <span className="text-lg font-bold">
                    {session.ai_confidence == null ? '—' : `${session.ai_confidence}%`}
                  </span>
                </div>
                <ConfidenceBar
                  value={session.ai_confidence || 0}
                  threshold={config.confidence_threshold ?? 75}
                  escalateAt={config.escalation_trigger ?? 40}
                  showLabel={false}
                />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This agent cites sources below{' '}
                <span className="text-foreground font-medium">
                  {config.confidence_threshold ?? 75}%
                </span>{' '}
                and escalates below{' '}
                <span className="text-foreground font-medium">
                  {config.escalation_trigger ?? 40}%
                </span>
                .
              </p>
            </CardContent>
          </Card>

          {session.escalation_reason && (
            <Card className="border-amber-500/20 bg-amber-500/5">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="w-4 h-4" />
                  <h3 className="text-sm font-semibold">Escalation Reason</h3>
                </div>
                <p className="text-sm leading-relaxed">{session.escalation_reason}</p>
                {session.escalated_at && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Escalated {formatWhen(session.escalated_at)}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {session.resolution_state === 'escalated' && (
            <Card className="border-amber-500/20 bg-amber-500/5 relative overflow-hidden">
              <CardContent className="p-5 relative">
                <Lightbulb className="absolute -top-2 -right-2 w-16 h-16 text-amber-500/10 -rotate-12" />
                <h3 className="text-sm font-semibold text-amber-600 dark:text-amber-400 mb-2">
                  Possible knowledge gap
                </h3>
                <p className="text-sm text-amber-900/80 dark:text-amber-200/80 leading-relaxed">
                  Low confidence here usually means the documentation does not cover this
                  question yet.
                </p>
                <Button variant="link" size="sm" className="text-amber-600 p-0 h-auto mt-3" asChild>
                  <Link to="/dashboard/agent/knowledge-gaps">Review knowledge gaps →</Link>
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4 text-brand-blue">
                <BookOpen className="w-4 h-4" />
                <h3 className="text-sm font-semibold">Source References</h3>
              </div>
              {sources.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No documents were retrieved for this conversation.
                </p>
              ) : (
                <div className="space-y-3">
                  {sources.map((ref) => (
                    <div key={ref.doc_id}>
                      <p className="text-sm font-medium">{ref.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Relevance {Math.round((ref.score || 0) * 100)}%
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>
              The transcript and all its messages are permanently removed. This also removes it
              from knowledge-gap analysis. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const MessageBubble = ({ message, config, onRate }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isArabic = message.language === 'ar';

  if (isSystem) {
    return (
      <div className="flex justify-center my-6">
        <div className="bg-muted/50 border border-border px-4 py-1.5 rounded-full text-xs text-muted-foreground">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex w-full mb-6 gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Bot className="w-4 h-4" />
        </div>
      )}
      <div className={cn('max-w-[78%] flex flex-col', isUser ? 'items-end' : 'items-start')}>
        <div
          className={cn(
            'px-4 py-3 rounded-2xl shadow-sm whitespace-pre-wrap',
            isUser
              ? 'bg-primary text-primary-foreground rounded-tr-none'
              : 'bg-card border border-border rounded-tl-none'
          )}
          dir={isArabic ? 'rtl' : 'ltr'}
        >
          <p className="text-sm leading-relaxed">{message.content}</p>
        </div>

        {!isUser && (
          <div className="mt-2 space-y-2 w-full">
            {message.confidence != null && (
              <ConfidenceBar
                value={message.confidence}
                threshold={config.confidence_threshold ?? 75}
                escalateAt={config.escalation_trigger ?? 40}
                className="max-w-[220px]"
              />
            )}
            {message.sources_used?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {message.sources_used.slice(0, 4).map((s) => (
                  <Badge key={s.doc_id} variant="secondary" className="text-[10px] font-normal">
                    {s.title}
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground">
                {new Date(message.created_at).toLocaleTimeString()}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-6 px-1.5',
                  message.feedback === 'positive' && 'text-emerald-500'
                )}
                onClick={() => onRate('positive')}
              >
                <ThumbsUp className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={cn('h-6 px-1.5', message.feedback === 'negative' && 'text-rose-500')}
                onClick={() => onRate('negative')}
              >
                <ThumbsDown className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {isUser && (
          <span className="text-[10px] text-muted-foreground mt-1">
            {new Date(message.created_at).toLocaleTimeString()}
          </span>
        )}
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
          <User className="w-4 h-4" />
        </div>
      )}
    </div>
  );
};

const Meta = ({ label, value, mono }) => (
  <div className="flex flex-col">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={cn('text-sm font-medium mt-0.5', mono && 'font-mono')}>{value}</span>
  </div>
);

export default TranscriptDetailPage;
